import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'

const MIN_SCALE = 0.5
const MAX_SCALE = 4
const SCALE_STEP = 1.25

interface ImageViewerProps {
  src: string
  alt: string
  /** Native pixel width — used for fit-width calculation */
  naturalWidth?: number
  /** Native pixel height */
  naturalHeight?: number
}

/**
 * ImageViewer — press-clipping / scan reader with zoom controls.
 *
 * Fits width by default; zoom in/out/reset. Overflow scrolls when zoomed.
 */
export default function ImageViewer({
  src,
  alt,
  naturalWidth,
  naturalHeight,
}: ImageViewerProps) {
  const { t } = useTranslation()
  const [scale, setScale] = useState(1)
  const [containerWidth, setContainerWidth] = useState(0)
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(
    naturalWidth && naturalHeight ? { w: naturalWidth, h: naturalHeight } : null,
  )
  const [loadError, setLoadError] = useState(false)
  const userZoomedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const update = () => setContainerWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const fitWidthScale = useMemo(() => {
    if (!imgSize || containerWidth === 0) return null
    // Reserve padding inside the scrollport
    return Math.min((containerWidth - 32) / imgSize.w, 1.5)
  }, [imgSize, containerWidth])

  useEffect(() => {
    if (!fitWidthScale || userZoomedRef.current) return
    setScale(fitWidthScale)
  }, [fitWidthScale])

  const zoomPercent = Math.round(scale * 100)
  const canZoomIn = scale < MAX_SCALE - 0.01
  const canZoomOut = scale > MIN_SCALE + 0.01

  const handleZoomIn = useCallback(() => {
    userZoomedRef.current = true
    setScale((s) => Math.min(MAX_SCALE, s * SCALE_STEP))
  }, [])

  const handleZoomOut = useCallback(() => {
    userZoomedRef.current = true
    setScale((s) => Math.max(MIN_SCALE, s / SCALE_STEP))
  }, [])

  const handleFitWidth = useCallback(() => {
    if (!fitWidthScale) return
    userZoomedRef.current = false
    setScale(fitWidthScale)
  }, [fitWidthScale])

  const displayW = imgSize ? Math.round(imgSize.w * scale) : undefined
  const displayH = imgSize ? Math.round(imgSize.h * scale) : undefined

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label={t('magazine.viewer.imageViewerLabel')}
      className="w-full"
    >
      {/* Toolbar */}
      <div className="sticky top-16 md:top-[110px] z-30 bg-surface-2 border border-border rounded-yada-lg shadow-yada-md p-3 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 ml-auto">
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={!canZoomOut}
            aria-label={t('magazine.viewer.zoomOut')}
            className="p-2 rounded-yada hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            <Icon icon="lucide:zoom-out" className="text-ink-soft text-lg" />
          </button>
          <span className="text-sm font-semibold text-ink w-12 text-center tabular-nums">
            {zoomPercent}%
          </span>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={!canZoomIn}
            aria-label={t('magazine.viewer.zoomIn')}
            className="p-2 rounded-yada hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            <Icon icon="lucide:zoom-in" className="text-ink-soft text-lg" />
          </button>
          <button
            type="button"
            onClick={handleFitWidth}
            disabled={!fitWidthScale}
            aria-label={t('magazine.viewer.fitWidth')}
            title={t('magazine.viewer.fitWidth')}
            className="p-2 rounded-yada hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            <Icon icon="lucide:maximize-2" className="text-ink-soft text-lg" />
          </button>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('magazine.viewer.openInNewTab')}
            title={t('magazine.viewer.openInNewTab')}
            className="p-2 rounded-yada hover:bg-surface transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            <Icon icon="lucide:external-link" className="text-ink-soft text-lg" />
          </a>
        </div>
      </div>

      {loadError ? (
        <div
          role="alert"
          className="bg-surface border-2 border-yada-coffee/40 rounded-yada-lg p-8 text-center"
        >
          <Icon
            icon="lucide:alert-triangle"
            className="text-yada-coffee mx-auto mb-3"
            style={{ fontSize: '2.5rem' }}
          />
          <p className="text-lg font-semibold text-ink mb-2">
            {t('magazine.viewer.imageErrorTitle')}
          </p>
          <p className="text-ink-soft">{t('magazine.viewer.imageErrorBody')}</p>
        </div>
      ) : (
        <div className="overflow-auto max-h-[80vh] bg-surface border border-border rounded-yada-lg p-4">
          <div className="mx-auto" style={{ width: displayW, minWidth: '100%' }}>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('magazine.viewer.openInNewTab')}
              title={t('magazine.viewer.openInNewTab')}
              className="block focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada"
            >
              <img
                src={src}
                alt={alt}
                width={naturalWidth}
                height={naturalHeight}
                decoding="async"
                onLoad={(e) => {
                  const img = e.currentTarget
                  setImgSize({ w: img.naturalWidth, h: img.naturalHeight })
                  setLoadError(false)
                }}
                onError={() => setLoadError(true)}
                style={{
                  width: displayW,
                  height: displayH,
                  maxWidth: 'none',
                }}
                className="block mx-auto shadow-yada-md"
              />
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
